/**
 * Mutable demo state for the contracts pages. Resets on page reload and via
 * `resetContractsDemo()`.
 */
import { createDemoStore } from '@/demo/core/create-demo-store';

export interface ContractsState {
  /** The uploaded Kestrel quality agreement was saved to the repository. */
  uploadSaved: boolean;
  /** Redlines for the uploaded agreement were sent to the supplier. */
  redlinesSent: boolean;
  /** Renewal events (`${contractId}:${kind}`) whose letter went to Legal. */
  lettersSentToLegal: string[];
  /** Contracts whose negotiation brief was shared with the category team. */
  briefsShared: string[];
  /** Net 45 amendment requests were sent for the short payment-term deals. */
  amendmentsRequested: boolean;
  /** Contracts whose reminder was set by the AI assistant; shown under Obligations. */
  remindersSet: string[];
}

function seed(): ContractsState {
  return {
    uploadSaved: false,
    redlinesSent: false,
    lettersSentToLegal: [],
    briefsShared: [],
    amendmentsRequested: false,
    remindersSet: [],
  };
}

export const contractsStore = createDemoStore(seed);

export function resetContractsDemo(): void {
  contractsStore.reset();
}

export function markUploadSaved(): void {
  contractsStore.update((draft) => {
    draft.uploadSaved = true;
  });
}

export function markRedlinesSent(): void {
  contractsStore.update((draft) => {
    draft.redlinesSent = true;
  });
}

export function markLetterSent(eventId: string): void {
  contractsStore.update((draft) => {
    if (!draft.lettersSentToLegal.includes(eventId)) {
      draft.lettersSentToLegal.push(eventId);
    }
  });
}

export function markBriefShared(contractId: string): void {
  contractsStore.update((draft) => {
    if (!draft.briefsShared.includes(contractId)) {
      draft.briefsShared.push(contractId);
    }
  });
}

export function markAmendmentsRequested(): void {
  contractsStore.update((draft) => {
    draft.amendmentsRequested = true;
  });
}

export function markReminderSet(contractId: string): void {
  contractsStore.update((draft) => {
    if (!draft.remindersSet.includes(contractId)) {
      draft.remindersSet.push(contractId);
    }
  });
}
